export default class CursorCircle {
    /** *
     * Initialises a circle div that follows the cursor. Then displays a message based on options
     * attached to an element e.g. data-
     *
     * @param {Object} options override options
     * @param {String} options.hex Hex code (excluding #) - note this will be overriden if
     * data-circle-colour is present on the circle-el
     * @param {String} options.defaultString The default text that will appear when no data-circle-string is present
     * on the circle-el
     * @param {Array} options.defaultClasses The classes to apply to circle by default
     * @param {Number} options.stringDelay The delay before the text is displayed
     * @param {Number | String} options.defaultSize The default size the circle will be when nothing is hovered.
     * @param {String} options.defaultHoverSize The default size that will appear if no data-circle-size is present
     * can be small, medium or large
     * @param {Number | String} options.smallSize The size (px) that the circle should appear as.
     * @param {Number | String} options.mediumSize The size (px) that the circle should appear as.
     * @param {Number | String} options.largeSize The size (px) that the circle should appear as.
     * @param {Object} options.anchorLinks What to do with anchor links with no circle-el class
     * @param {Boolean} options.anchorLinks.interact Whether or not the circle should interact with anchor tags with no circle-el
     * class
     * @param {String} options.anchorLinks.hoverSize small, medium or large for these elements small by default
     * @param {String} options.anchorLinks.string A custom string if you hover this element default is empty ("")
     * @param {Array} options.anchorLinks.exclusions classes/ids to ignore.
     */
    constructor(options = {}) {
        this.options = {
            hex: options.hex ?? '#000000',
            defaultString: options.defaultString ?? 'View post',
            stringDelay: options.stringDelay ?? 250,
            defaultSize: options.defaultSize ?? '40px',
            defaultHoverSize: options.defaultHoverSize ?? 'small',
            smallSize: options.smallSize ?? '80px',
            mediumSize: options.mediumSize ?? '115px',
            largeSize: options.largeSize ?? '150px',
            anchorLinks: options.anchorLinks ?? {
                interact: true,
                hoverSize: 'small',
                string: '',
                // exclusions: []
            }
        }

        this.validateFields();

        //the position of the cursor
        this.mouseX = 0;
        this.mouseY = 0;

        //last position of the cursor
        this.prevX = -1;
        this.prevY = -1;

        //the offset to subtract from the mouseX and the mouseY
        this.offset = this.options.largeSize / 2;

        //the text that should appear in the circle, leave blank if not hovering anything.
        this.currentString = "";

        //the size of the circle ie. small, medium or large. None should be used if nothing is currently
        //hovered.
        this.currentSize = 'none'

        //the classes to be applied to the circle
        this.classes = options.defaultClasses ?? ['cursor-circle'];

        //getting the default hex and setting this as the current hex, this will be changable on hover.
        this.currentHex = this.options.hex;

        this.initialiseCircle();
        this.initialiseElements();

        //when the user moves the mouse move the circle to it's position.q
        window.addEventListener('mousemove', e => {
            this.calculatePosition(e);

            if(navigator.userAgent.indexOf('MSIE')!==-1
                || navigator.appVersion.indexOf('Trident/') > -1) {
                this.updateCircle()
            } else {
                //performing any circle style updates in requestAnimationFrame as
                //this is what I believe made Class's one so laggy, and apparently,
                //this will reduce the main thread usage.
                window.requestAnimationFrame(() => {
                    this.updateCircle()
                })
            }
        });
    }

    initialiseElements() {
        //include regular anchors if this option has been selected, else just do circle-el's.
        this.circleEls = this.getQuery();

        //if there are no circleEls to be triggered on the page, provide a warning
        if(!this.circleEls) {
            console.warn('cursor-circle.js - No elements with class circle-el on this page.')
        }

        Array.from(this.circleEls).forEach(circleEl => {
            circleEl.removeEventListener('mouseenter', e => { this.elHover(e, circleEl) });
            circleEl.removeEventListener('mouseleave', e => { this.elExit(e, circleEl) });

            circleEl.addEventListener('mouseenter', e => { this.elHover(e, circleEl) });
            circleEl.addEventListener('mouseleave', e => { this.elExit(e, circleEl) })
        })
    }

    getQuery() {
        let query = '';
        if(this.options.anchorLinks.interact) {
            //ie11 only
            if(navigator.userAgent.indexOf('MSIE')!==-1
                || navigator.appVersion.indexOf('Trident/') > -1){
                let anchorExclusions = [];
                let buttonExclusions = [];
                this.options.anchorLinks.exclusions.forEach(exclusion => {
                    anchorExclusions.push(exclusion);
                    buttonExclusions.push(exclusion)
                });
                let stringifyExceptions = (exceptions) => {
                    let exceptionString = "";
                    exceptions.forEach(exception => {
                        exceptionString += ":not(" + exception + ")"
                    });
                    return exceptionString;
                }
                let anchorSelector = 'a:not(.circle-el, .lang-item a)' + stringifyExceptions(anchorExclusions);
                let buttonSelector = 'button:not(.circle-el, .lang-item a)' + stringifyExceptions(buttonExclusions);
                query = ".circle-el, " + anchorSelector + ", " + buttonSelector;
            } else {
                query = ".circle-el, " +
                    "a:not(.circle-el, .lang-item a, .circle-el a, " + this.options.anchorLinks.exclusions.join(", ") + "), " +
                    "button:not(.circle-el, .circle-el button, " + this.options.anchorLinks.exclusions.join(", ") + ")";
            }
        } else {
            query = '.circle-el';
        }

        // console.log("Cursor-circle.js - Query: " + query);
        let els = document.querySelectorAll(query);

        if(navigator.userAgent.indexOf('MSIE')!==-1
            || navigator.appVersion.indexOf('Trident/') > -1){
            let anchorEls = Array.from(document.querySelectorAll('.circle-el a'));
            let buttonEls = Array.from(document.querySelectorAll('.circle-el button'));
            let newEls = Array.from(els).filter(el => !anchorEls.includes(el) && !buttonEls.includes(el));
            return newEls;
        }

        return els;
    }

    /**
     *  When we begin hovering change the size, text etc.
     * @param e the event
     * @param {HTMLElement} circleEl The element that has been hovered
     */
    elHover(e, circleEl) {
        this.circleElHoverStart(e, circleEl);
        if(navigator.userAgent.indexOf('MSIE')!==-1
            || navigator.appVersion.indexOf('Trident/') > -1) {
            this.updateCircle()
        } else {
            window.requestAnimationFrame(() => {
                this.updateCircle()
            })
        }

        //updating the classlist after the specified delay period.
        if(this.options.stringDelay && typeof this.options.stringDelay === "number") {
            setTimeout(function() {
                this.classes.indexOf('cursor-circle--expanded') === -1 ? this.classes.push('cursor-circle--expanded') : '';
                if(navigator.userAgent.indexOf('MSIE')!==-1
                    || navigator.appVersion.indexOf('Trident/') > -1) {
                    this.updateCircle.bind(this)
                } else {
                    window.requestAnimationFrame(() => {
                        this.updateCircle.bind(this)
                    })
                }
            }.bind(this), this.options.stringDelay);
        }
    }

    /**
     * Revert the circle to its default state.
     * @param e the event
     * @param {HTMLElement} circleEl The element that is no longer hovered
     */
    elExit(e, circleEl) {
        this.circleElHoverEnd(e, circleEl);
        if(navigator.userAgent.indexOf('MSIE')!==-1
            || navigator.appVersion.indexOf('Trident/') > -1) {
            this.updateCircle()
        } else {
            window.requestAnimationFrame(() => {
                this.updateCircle()
            })
        }
    }

    /** Setting the data to be that in the dataset if a circle-el or the
     * anchorLinks option if it is a plain anchor tag.
     * @param e Event
     * @param {HTMLElement} el Element to compare e.target against.
     * */
    circleElHoverStart(e, el) {
        if(el === e.target) {
            if(el.classList.contains('circle-el')) {
                // console.log(el.dataset.circleColour);
                //updating the size, colour and text by either the defaults or those specified in the
                //dataset.
                this.currentSize = el.dataset.circleSize ?? this.options.defaultHoverSize;
                this.currentHex = el.dataset.circleColour ?? this.options.hex;
                this.currentString = el.dataset.circleText ?? this.options.defaultString;
            //if the hovered element is a regular anchor tag, no circle-el class.
            } else {
                this.currentSize = this.options.anchorLinks.hoverSize;
                this.currentHex = this.options.hex;
                this.currentString = this.options.anchorLinks.string;
            }
        }
    }

    /** Removing the current hover data.
     * @param e Event
     * @param {HTMLElement} el Element to compare e.target against.
     * */
    circleElHoverEnd(e, el) {
        if(el === e.target) {
            this.currentString = "";
            this.currentSize = "none";
            this.currentHex = this.options.hex;
            let expanded = this.classes.indexOf('cursor-circle--expanded');
            if(expanded !== -1) {
                this.classes.splice(expanded, 1);
            }
        }
    }

    initialiseCircle() {
        this.circleWrapper = document.createElement('div');
        this.circleWrapper.classList.add('cursor-circle-wrapper');
        this.circleWrapper.role = "presentation none";
        this.circleWrapper.style.width = this.options.largeSize + 'px';
        this.circleWrapper.style.height = this.options.largeSize + 'px';

        this.circle = document.createElement('div');
        this.circle.classList.add('cursor-circle');
        this.circle.style.backgroundColor = this.options.hex;
        this.circle.style.width = this.options.defaultSize + 'px';
        this.circle.style.height = this.options.defaultSize + 'px';
        this.circleWrapper.appendChild(this.circle)

        document.body.insertBefore(this.circleWrapper, document.body.firstChild);
    }

    /**
     * Calculating new position of the circle based on the current circle location.
     * */
    calculatePosition(e) {
        this.mouseY = e.pageY;
        this.mouseX = e.pageX;
    }

    /**
     * Performing all necessary style/text changes to the circle.
     */
    updateCircle() {
        let newClasses = this.classes.join(" ");
        if(this.circle.classList.value !== newClasses) {
            this.circle.className = newClasses;
        }

        let sizeToUse = this.currentSize === 'none' ? this.options.defaultSize : this.options[this.currentSize + 'Size'];
        let newHeight = sizeToUse + "px";
        let newWidth = sizeToUse + "px";
        if(newWidth !== this.circle.style.width || newHeight !== this.circle.style.height) {
            this.circle.style.width = newWidth;
            this.circle.style.height = newHeight;
        }

        if(this.circle.style.backgroundColor !== this.currentHex) {
            this.circle.style.backgroundColor = this.currentHex;
        }

        let currentSpan = this.circle.firstElementChild;
        if(!currentSpan) {
            this.circle.innerHTML = "<span></span>";
            currentSpan = this.circle.firstElementChild;
        }

        if(currentSpan && currentSpan.innerHTML !== this.currentString) {
            // console.log("Old Span was: " + currentSpan.innerHTML + " New Span is: " + this.currentString);
            this.circle.innerHTML = "<span>" + this.currentString + "</span>";
        }

        let newY = this.mouseY - this.offset;
        let newX = this.mouseX - this.offset;

        if(newX !== this.prevX || newY !== this.prevY) {
            // console.log(`CursorCircle - ${new Date()} X: ${newX}px Y: ${newY}px Y: `)
            this.circleWrapper.style.transform = `translateY(${newY}px) translateX(${newX}px) rotate(0.0001deg)`;
            // this.circleWrapper.style.MozTransform = `translateY(${newY}px) translateX(${newX}px) rotate(0.02deg)`;
            this.prevX = newX;
            this.prevY = newY;
        }
    }

    getTranslateXY(element) {
        const style = window.getComputedStyle(element)
        const matrix = new DOMMatrixReadOnly(style.transform)
        return {
            translateX: matrix.m41,
            translateY: matrix.m42
        }
    }

    validateFields() {
        //appending a # to the string.
        if(!this.options.hex.includes('#')) {
            this.options.hex = '#' + this.options.hex;
        }
        let validColour = this.validateColour(this.options.hex);
        if(!validColour) {
            throw new Error("cursor-circle.js - " + this.options.hex + " is not a valid hex colour");
        }

        let validSize = this.validateSize(this.options.defaultSize);
        if(!validSize.status) {
            throw new Error("cursor-circle.js - " + validSize.message);
        }

        this.options.defaultSize = validSize.number;

        validSize = this.validateSize(this.options.smallSize);
        if(!validSize.status) {
            throw new Error("cursor-circle.js - " + validSize.message);
        }

        this.options.smallSize = validSize.number;

        validSize = this.validateSize(this.options.mediumSize);
        if(!validSize.status) {
            throw new Error("cursor-circle.js - " + validSize.message);
        }

        this.options.mediumSize = validSize.number;

        validSize = this.validateSize(this.options.largeSize);
        if(!validSize.status) {
            throw new Error("cursor-circle.js - " + validSize.message);
        }

        this.options.largeSize = validSize.number;

        //ensuring the defaultHoverSize is small medium or large
        if(typeof this.options.defaultHoverSize === 'string') {
            this.options.defaultHoverSize = this.options.defaultHoverSize.toLowerCase();
            if(this.options.defaultHoverSize !== 'small' && this.options.defaultHoverSize !== 'medium' && this.options.defaultHoverSize !== 'large') {
                throw new Error('cursor-circle.js - defaultHoverSize must be small, medium or large');
            }
        } else {
            throw new Error('cursor-circle.js - defaultHoverSize must be small, medium or large');
        }
    }

    /**
     * Validates hex value
     * @param  {String} color hex color value
     * @return {Boolean}
     */
    validateColour(color) {
        if(!color || typeof color !== 'string') return false;

        // Validate hex values
        if(color.substring(0, 1) === '#') {
            color = color.substring(1);
        }


        switch(color.length) {
            case 3: return /^[0-9A-F]{3}$/i.test(color);
            case 6: return /^[0-9A-F]{6}$/i.test(color);
            case 8: return /^[0-9A-F]{8}$/i.test(color);
            default: return false;
        }

        return false;
    }

    /**
     * @param {Number | String} size size of the string
     * @return {Object} Whether or not the size is a valid number / string
     */
    validateSize(size) {
        if(typeof size === 'number') {
            //ensuring the number is above 0 as we cannot have a negative sized
            //circle
            if(size <= 0) {
                return {
                    status: false,
                    number: null,
                    message: "Number must be above 0.",
                }
            }

            //return if already a number.
            return {
                status: true,
                number: size,
            };
        }

        //if the size is a number, convert to a number then return this.
        if(typeof size === 'string') {
            //list of units other than px
            let invalidSizes = [
                'cm',
                'mm',
                'in',
                'pt',
                'pc',
                'em',
                'ex',
                'ch',
                'rem',
                'vw',
                'vh',
                'vmin',
                'vmax',
                '%',
            ];

            //check for invalid sizes e.g. rem, em, % etc. if the string contains this, throw
            //a warning but convert to a number anyways.
            if(invalidSizes.some(substring => size.includes(substring))) {
                console.warn('cursor-circle.js - ' + size + " is not px but another unit, either remove the unit or change to px." +
                    "We will use this as px regardless.");
            }

            //converting string to number
            let number = parseInt(size);

            //if this is NaN then no number was present in the string.
            if(!number || isNaN(number)) {
                return {
                    status: false,
                    number: null,
                    message: 'The size does not contain a number',
                }
            }

            //the HTML element cannot be below 0px.
            if(number <= 0) {
                return {
                    status: false,
                    number: null,
                    message: "Number must be above 0.",
                }
            }

            //valid number, return
            return {
                status: true,
                number: number,
            }
        }

        //if not a number or a string.
        return {
            status: false,
            number: null,
            message: "Size is neither a string or a number"
        };
    }

    /** Call from outside this class to update any options you may have changed */
    update() {
        if(navigator.userAgent.indexOf('MSIE')!==-1
            || navigator.appVersion.indexOf('Trident/') > -1) {
            this.updateCircle()
        } else {
            window.requestAnimationFrame(() => {
                this.updateCircle()
            })
        }
    }

    /**
     * preloading image from data-circle-preload
     * @param {HTMLElement} el CircleEl to preload image for.
     */
    preloadImage(el) {
        if(el.dataset.circlePreload) {
            let src = el.dataset.circlePreload;
            try {
                const img = new Image();
                img.src = src;
            } catch(e) {
                console.log("Cursor-circle.js - failed to preload " + src);
            }
        }
    }
}